import { Box, TableCell, TableRow } from '@mui/material';
import { FC, useContext } from 'react';
import MediumTypography from '../../components/formlib/MediumTypography';
import { ReactComponent as ViewEye } from '../../assets/images/ViewEye.svg';
import { ReactComponent as Download } from '../../assets/images/downloadIcon.svg';
import ToolTipIcon from '../../components/formlib/ToolTipIcon';
import TooltipTableCell from '../../components/formlib/TooltipTableCell';
import {
  ConsentListData,
  ConsentInsurance,
} from '../../services/configApi/forms/Consent/ConsentServices';
import moment from 'moment';
import { ReactComponent as ShareIcon } from '../../assets/images/shareIcon.svg';
import CheckBoxComponent from '../../components/formlib/CheckBoxComponent';
import { ReactComponent as DeleteIcon } from '../../assets/images/DeleteIcon.svg';
import { checkPermissionForFeature } from '../../utils/checkPermission';
import { LookUpContext } from '../../context/LookUpContextProvider';

interface ConsentProps {
  data: ConsentListData;
  onDelete?: (rowData: ConsentListData) => void;
  onView: (rowData: ConsentListData) => void;
  onDownload: (rowData: ConsentListData) => void;
  onShare: (rowData: ConsentListData) => void;
  offlineFlag?: boolean;
   hasEditPermission: boolean;
}

const ConsentTableRow: FC<ConsentProps> = (props) => {
  const { consentName, consentDate, consentType, status, permissionGrantedTo } =
    props.data;

  const { lookups } = useContext(LookUpContext);
  
  const consentInsurance = props.data?.consentInsurance;
  let insuranceList: (ConsentInsurance & []) | ConsentInsurance[] = [];

  if (consentInsurance) {
    insuranceList = Array.isArray(consentInsurance)
      ? consentInsurance
      : [consentInsurance];
  }
  
 
  const getInsuranceDescription = (ins: ConsentInsurance) => {
    if (ins?.companyId === 'backend-others') {
      return ins?.otherCompanyName;
    }

    const match = lookups?.consentInsurances.find(
      (item) => item.insuranceBackendId === ins?.companyId,
    );

    return match?.description;
  };

  const insuranceDescriptions = insuranceList
    .map(getInsuranceDescription)
    .filter(Boolean);

  let displayValue = consentName;
  if (consentType === 'InsuranceConsent' && insuranceList.length > 0) {
    const suffix =
      insuranceDescriptions.length > 0
        ? ` - ${insuranceDescriptions.join(', ')}`
        : '';

    displayValue = `${consentName}${suffix}`;
  }
  const renderCell = (
    content: string | null | undefined,
    format?: string,
    textColor?: string,
  ) => {
    if (!content || content === '') {
      return (
        <TableCell sx={{ padding: '0px' }}>
          <MediumTypography label="-" sxProps={{ textAlign: 'left' }} />
        </TableCell>
      );
    } else if (content === 'N/A') {
      return <MediumTypography label="N/A" sxProps={{ textAlign: 'left' }} />;
    }
    return (
      <MediumTypography
        label={format ? moment(content).local().format(format) : content}
        textColor={textColor}
      />
    );
  };

  const renderStatusCell = () => {
    let textColor: string | undefined;
    switch (status) {
      case 'Revoked':
      case 'Expired':
        textColor = '#EB4C60';
        break;
      case 'Current':
        textColor = '#2A4241';
        break;
      default:
        textColor = '#97A6A5';
        break;
    }

    return renderCell(status, undefined, textColor);
  };

  return (
    <TableRow
      hover
      sx={{
        cursor: 'pointer',
        alignContent: 'flex-start',
        '&:nth-of-type(odd)': {
          backgroundColor: '#ECF9F8',
        },
      }}
    >
      <TableCell padding="checkbox">
        <CheckBoxComponent ischecked={false} />
      </TableCell>

      <TooltipTableCell
        value={displayValue}
        visible={true}
        textSxProps={{ maxWidth: '400px' }}
      />
      <TableCell>{renderCell(consentDate, 'MM/DD/YYYY')}</TableCell>
      <TableCell>{renderStatusCell()}</TableCell>
      <TooltipTableCell
        value={permissionGrantedTo || 'N/A'}
        visible={true}
        textSxProps={{ maxWidth: '200px' }}
      />
      <TableCell>
        <Box className="flex__">
          <Box
            className="cursorPointer"
            onClick={() => props.onView(props.data)}
          >
            <ToolTipIcon
              labelId="ConsentForms.listActionViewToolTipLabel"
              defaultLabel="View"
              cursor="pointer"
              Icon={<ViewEye />}
            />
          </Box>
          {!props.offlineFlag && (
            <Box
              className="cursorPointer"
              onClick={() => props.onShare(props.data)}
            >
              <ToolTipIcon
                labelId="ConsentForms.listActionShareToolTipLabel"
                defaultLabel="Share"
                cursor="pointer"
                Icon={<ShareIcon />}
              />
            </Box>
          )}
          {!props.offlineFlag && (
            <Box
              className="cursorPointer"
              onClick={() => props.onDownload(props.data)}
            >
              <ToolTipIcon
                labelId="ConsentForms.listActionDownloadToolTipLabel"
                defaultLabel="Download"
                cursor="pointer"
                Icon={<Download />}
              />
            </Box>
          )}
          {!props.offlineFlag &&
            props.hasEditPermission &&
            (props.data.isSigned
              ? checkPermissionForFeature(
                  'backend.consents',
                  'deletePermission',
                ) &&
                checkPermissionForFeature('backend.clients', 'editPermission')
              : true) && (
              <Box className="cursorPointer">
                <ToolTipIcon
                  labelId="ConsentForms.listActionDeleteToolTipLabel"
                  defaultLabel="Delete"
                  Icon={
                    <DeleteIcon
                      onClick={() =>
                        props.onDelete?.(props.data)
                      }
                    />
                  }
                />
              </Box>
            )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default ConsentTableRow;
